import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Use window.location.origin for API calls to work on any domain (preview or custom)
// This ensures the API calls go to the same domain the frontend is served from
const API = `${window.location.origin}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Send user's timezone to the backend
const sendTimezoneToBackend = async () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    await fetch(`${API}/auth/timezone`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ timezone }),
    });
    console.log('Timezone sent to backend:', timezone);
  } catch (error) {
    console.error('Failed to send timezone:', error);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        // Send timezone to backend when user is authenticated
        sendTimezoneToBackend();
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    setUser(data);
    // Send timezone after successful login
    sendTimezoneToBackend();
    return data;
  };

  const register = async (email, password, name) => {
    console.log('[AuthContext] Attempting registration for:', email);
    
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name }),
      });

      console.log('[AuthContext] Registration response status:', response.status);

      if (!response.ok) {
        const error = await response.json();
        console.error('[AuthContext] Registration error:', error);
        throw new Error(error.detail || 'Registration failed');
      }

      const data = await response.json();
      console.log('[AuthContext] Registration successful, user:', data.email);
      setUser(data);
      // Send timezone after successful registration
      sendTimezoneToBackend();
      return data;
    } catch (error) {
      console.error('[AuthContext] Registration network/parse error:', error);
      throw error;
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await fetch(`${API}/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ session_id: sessionId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Session exchange failed');
    }

    const data = await response.json();
    setUser(data);
    // Send timezone after Google OAuth login
    sendTimezoneToBackend();
    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const deleteAccount = async () => {
    const response = await fetch(`${API}/auth/delete-account`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Account deletion failed');
    }

    setUser(null);
  };

  const updateUser = (updates) => {
    setUser((prev) => prev ? { ...prev, ...updates } : null);
  };

  const updateCurrency = async (currency) => {
    try {
      const response = await fetch(`${API}/auth/currency`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currency }),
      });

      if (response.ok) {
        setUser((prev) => prev ? { ...prev, currency } : null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update currency:', error);
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    exchangeSession,
    logout,
    deleteAccount,
    updateUser,
    updateCurrency,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
